import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const InstantProjectScoping = ({ onScope }) => {
  const [formData, setFormData] = useState({
    companySize: '',
    industry: '',
    gccSize: '',
    complexity: '',
    timeline: ''
  });
  const [estimate, setEstimate] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small (51-200 employees)' },
    { value: 'medium', label: 'Medium (201-1000 employees)' },
    { value: 'large', label: 'Large (1001-5000 employees)' },
    { value: 'enterprise', label: 'Enterprise (5000+ employees)' }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'consulting', label: 'Consulting & Professional Services' }
  ];

  const gccSizeOptions = [
    { value: 'small', label: 'Small GCC (50-200 employees)' },
    { value: 'medium', label: 'Medium GCC (200-500 employees)' },
    { value: 'large', label: 'Large GCC (500-1000 employees)' },
    { value: 'enterprise', label: 'Enterprise GCC (1000+ employees)' }
  ];

  const complexityOptions = [
    { value: 'basic', label: 'Basic Setup (Standard processes)' },
    { value: 'moderate', label: 'Moderate (Custom integrations)' },
    { value: 'complex', label: 'Complex (Multi-location, specialized)' },
    { value: 'enterprise', label: 'Enterprise (Global integration)' }
  ];

  const timelineOptions = [
    { value: 'aggressive', label: 'Aggressive (3-6 months)' },
    { value: 'standard', label: 'Standard (6-12 months)' },
    { value: 'conservative', label: 'Conservative (12-18 months)' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateEstimate = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay
    setTimeout(() => {
      const baseTimeline = {
        aggressive: 4,
        standard: 8,
        conservative: 12
      };

      const complexityMultiplier = {
        basic: 1,
        moderate: 1.2,
        complex: 1.5,
        enterprise: 1.8
      };

      const sizeMultiplier = {
        small: 1,
        medium: 1.3,
        large: 1.6,
        enterprise: 2
      };

      const base = baseTimeline[formData.timeline] || 8;
      const complexity = complexityMultiplier[formData.complexity] || 1;
      const size = sizeMultiplier[formData.gccSize] || 1;

      const totalMonths = Math.round(base * complexity * size);
      const estimatedCost = totalMonths * 85000; // Base cost per month

      const estimateData = {
        timeline: totalMonths,
        cost: estimatedCost,
        phases: [
          { name: 'Strategic Planning', duration: Math.round(totalMonths * 0.15) },
          { name: 'Location & Legal', duration: Math.round(totalMonths * 0.25) },
          { name: 'Talent Acquisition', duration: Math.round(totalMonths * 0.35) },
          { name: 'Operations Setup', duration: Math.round(totalMonths * 0.25) }
        ]
      };

      setEstimate(estimateData);
      onScope?.(estimateData);
      setIsCalculating(false);
    }, 2000);
  };

  const isFormValid = Object.values(formData).every(value => value !== '');

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
          <Icon name="Clock" size={32} color="white" />
        </div>
        <h3 className="text-2xl font-headline font-bold text-text-primary mb-2">
          Instant Project Scoping
        </h3>
        <p className="text-text-secondary max-w-2xl mx-auto">
          Get a customized timeline and cost estimate for your GCC establishment project based on your specific requirements and complexity.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <div 
          className="rounded-xl p-8 shadow-executive"
          style={{
            backgroundColor: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-card-foreground)'
          }}
        >
          <h4 
            className="text-xl font-headline font-semibold mb-6"
            style={{ color: 'var(--color-primary)' }}
          >
            Project Requirements
          </h4>

          <div className="space-y-6">
            <Select
              label="Company Size"
              options={companySizeOptions}
              value={formData.companySize}
              onChange={(value) => handleInputChange('companySize', value)}
              placeholder="Select your company size"
            />

            <Select
              label="Industry"
              options={industryOptions}
              value={formData.industry}
              onChange={(value) => handleInputChange('industry', value)}
              placeholder="Select your industry"
            />

            <Select
              label="Planned GCC Size"
              options={gccSizeOptions}
              value={formData.gccSize}
              onChange={(value) => handleInputChange('gccSize', value)}
              placeholder="Select planned GCC size"
            />

            <Select
              label="Project Complexity"
              options={complexityOptions}
              value={formData.complexity}
              onChange={(value) => handleInputChange('complexity', value)}
              placeholder="Select complexity level"
            />

            <Select
              label="Preferred Timeline"
              options={timelineOptions}
              value={formData.timeline}
              onChange={(value) => handleInputChange('timeline', value)}
              placeholder="Select preferred timeline"
            />

            <Button
              variant="default"
              size="lg"
              fullWidth
              className="bg-cta hover:bg-cta/90 text-cta-foreground"
              iconName="Calculator"
              iconPosition="left"
              loading={isCalculating}
              disabled={!isFormValid}
              onClick={calculateEstimate}
            >
              {isCalculating ? 'Calculating...' : 'Calculate Estimate'}
            </Button>
          </div>
        </div>

        {/* Results Section */}
        <div 
          className="rounded-xl p-8 shadow-executive"
          style={{
            backgroundColor: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-card-foreground)'
          }}
        >
          <h4 
            className="text-xl font-headline font-semibold mb-6"
            style={{ color: 'var(--color-primary)' }}
          >
            Your Project Estimate
          </h4>

          {!estimate ? (
            <div className="text-center py-12">
              <Icon 
                name="Calculator" 
                size={48} 
                color="var(--color-text-secondary)" 
                className="mx-auto mb-4" 
              />
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Complete the form to get your customized estimate
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className="rounded-lg p-4 text-center"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  <div 
                    className="text-2xl font-bold"
                    style={{ color: 'var(--color-primary-foreground)' }}
                  >
                    {estimate.timeline}
                  </div>
                  <div style={{ color: 'var(--color-primary-foreground)' }}>Months</div>
                </div>
                <div 
                  className="rounded-lg p-4 text-center"
                  style={{ backgroundColor: 'var(--color-accent)' }}
                >
                  <div 
                    className="text-2xl font-bold"
                    style={{ color: 'var(--color-accent-foreground)' }}
                  >
                    ${(estimate.cost / 1000).toFixed(0)}K
                  </div>
                  <div style={{ color: 'var(--color-accent-foreground)' }}>Estimated Cost</div>
                </div>
              </div>

              <div>
                <h5 
                  className="font-medium mb-3"
                  style={{ color: 'var(--color-primary)' }}
                >
                  Phase Breakdown:
                </h5>
                <div className="space-y-2">
                  {estimate.phases.map((phase, index) => (
                    <div 
                      key={index} 
                      className="flex justify-between items-center py-2 border-b last:border-b-0"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <span 
                        className="text-sm"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {phase.name}
                      </span>
                      <span 
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-accent)' }}
                      >
                        {phase.duration} months
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className="rounded-lg p-4"
                style={{ backgroundColor: 'var(--color-success)' }}
              >
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="CheckCircle" size={16} color="var(--color-success-foreground)" />
                  <span 
                    className="text-sm font-medium"
                    style={{ color: 'var(--color-success-foreground)' }}
                  >
                    Estimate Includes
                  </span>
                </div>
                <ul 
                  className="text-sm space-y-1"
                  style={{ color: 'var(--color-success-foreground)' }}
                >
                  <li>• End-to-end project management</li>
                  <li>• Legal and compliance support</li>
                  <li>• Talent acquisition assistance</li>
                  <li>• 6-month post-launch support</li>
                </ul>
              </div>

              <Button
                variant="outline"
                size="default"
                fullWidth
                iconName="Download"
                iconPosition="left"
              >
                Download Detailed Estimate
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstantProjectScoping;